//@ts-ignore
import { isEqual } from "lodash-es";
import API from "Classes/Api";
import {
  ApiDeleteCallParams,
  ApiGetCallParams,
  ApiPostCallParams,
  ApiPutCallParams,
} from "Classes/ApiCallParams";
import { HubConnectionBuilder } from "@microsoft/signalr";
import AuthenticationService from "services/AuthService/Authentication";
import LookupService from "services/LookupService";
//@ts-ignore
import { FORTIFIED_DATA_API_URL } from "config";
import authTypes from "reduxStore/auth/auth.types";
import { setSignalRconnection } from "reduxStore/api/api.actions";
import { ClientInfoSaveParams, NewClientSaveParams } from "types/app";
import { setAllowedClients, setPermissions } from "reduxStore/auth/auth.actions";
import {
  setClient,
  setClientID,
  updateTopFilter,
  updateTopFilterValues,
} from "reduxStore/globalFilter/globalFilter.actions";
import { store } from "reduxStore/store";
import { GetCollectorEnvironmentsResponse } from "../ServicesTypes/Environment";
import { GetUserNotificationsResponse } from "services/ServicesTypes/Clients";

class Clients extends API {
  async getClients({ clientId, filters }: any) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/dashboards/enterprise/overview`,
        body: {
          filters,
        },
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to load Clients.",
      })
    );

    return result;
  }

  async getClient(clientId: number) {
    try {
      const result = await super.call(
        new ApiGetCallParams({
          action: `clients/client_info/${clientId}`,
          headers: { ...API.getAuthHeaders() },
        })
      );

      return result;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async getPermissions(clientId: number) {
    try {
      const result = await super.call(
        new ApiGetCallParams({
          action: `permissions/clients/${clientId}`,
          headers: { ...API.getAuthHeaders() },
        })
      );

      return result;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async getCollectorEnvironments(clientId: any) {
    const query = {};
    const result: GetCollectorEnvironmentsResponse = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/collector-environments`,
        headers: { ...API.getAuthHeaders() },
        query,
      })
    );

    // Modify for v2 api
    const data = result.environments;
    return { data };
  }

  async addClient(params: Partial<NewClientSaveParams>) {
    if ((params as any).ticketingSystemId) {
      (params as any).ticketingSystemId = parseInt((params as any).ticketingSystemId as string, 10);
    }
    const result = await super.call(
      new ApiPutCallParams({
        action: `client`,
        headers: { ...API.getAuthHeaders() },
        body: { data: params },
        successMessage: "Account successfully created.",
      })
    );

    return result;
  }

  async updateClient(clientId: number, params: ClientInfoSaveParams) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `clients/${clientId}`,
        headers: { ...API.getAuthHeaders() },
        body: { data: params },
        successMessage: "Account successfully updated.",
      })
    );
    await this.loadGlobalClient(clientId); //reload the client - TODO: when implementing the cache validation remove this

    return result;
  }

  async deleteClient(clientId: number) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `clientsl/${clientId}`,
        headers: { ...API.getAuthHeaders() },
        successMessage: "Client deletion request queued for processing.",
      })
    );

    return result; // expected- null
  }

  async loadGlobalClient(clientId: number) {
    const state: any = store.getState();
    const isSubscriptionExpired = state.auth?.currentUser?.isSubscriptionExpired;

    if (isSubscriptionExpired) {
      const redirectUrl = `/subscription-expired`;
      window.location.href = redirectUrl;
    }

    if (clientId !== null) {
      window.localStorage.setItem("clientid", `${clientId}`);
    }
    store.dispatch(setClientID({ clientId }));
    store.dispatch(updateTopFilter(null)); //reset the top filter
    store.dispatch(updateTopFilterValues(null)); //reset the top filter values

    if (clientId && clientId > 0) {
      const clientResult: any = await this.getClient(clientId);

      let permissions = await this.getPermissions(clientId);

      if (permissions?.isAborted || permissions?.errors) {
        permissions = await this.getPermissions(clientId); //retry
      }

      if (permissions && !permissions?.errors && Object.keys(permissions)?.length > 0) {
        store.dispatch(setPermissions({ permissions }));
      } else {
        console.error(`Error occured on getPermissions no permissions for ${clientId}`);
        // debugger;
      }

      if (clientResult && clientResult.data) {
        store.dispatch(setClient({ clientInfo: clientResult.data }));
        return clientResult?.data;
      }
    } else {
      console.error("client id is invalid");
      store.dispatch(setClient({ clientInfo: {} }));
    }
    return {};
  }

  registerCacheHub(clientId: number) {
    const state: any = store.getState();

    const connection = new HubConnectionBuilder()
      .withUrl(`${FORTIFIED_DATA_API_URL}/api_hub?clientid=${clientId}`)
      .withAutomaticReconnect([0, 3000, 5000, 10000, 15000, 30000, 60000])
      .build();

    const initConnection = () => {
      connection.start().then(() => {
        store.dispatch(setSignalRconnection(connection));
      });

      connection.on("broadcastMessage", async (message): Promise<void> => {
        if (typeof message === "object" && message?.CacheName) {
          // local cache deletion
          const messageClientId = message?.ID;
          const currentClientId = state.globalFilter.clientId;

          switch (message?.CacheName) {
            case "Hosts":
            case "DatabaseInstances":
              if (messageClientId == currentClientId) {
                LookupService.removeCache(message?.CacheName, clientId);
              }
              break;

            default:
              console.error(`broadcastMessage- Unknown message: ${message}`);
              break;
          }
        } else if (typeof message === "string") {
          if (message?.startsWith("client-")) {
            const messageClientId = parseInt(message?.replace("client-", ""));
            const currentClientId = state.globalFilter.clientId;
            if (messageClientId === currentClientId) {
              this.getClient(clientId).then((clientResult) => {
                const currentClient = state.globalFilter.clientInfo;
                if (!currentClient.errors) {
                  const clientInfo = clientResult.data;

                  if (!isEqual(clientInfo, currentClient)) {
                    store.dispatch(setClient({ clientInfo }));
                  }
                }
              }); //reload client

              AuthenticationService.getAllowedClients().then((allowedClientsResponse) => {
                const currentAllowedClients = state.auth.allowedClients; // current loggedin user

                if (
                  !allowedClientsResponse.errors &&
                  !isEqual(currentAllowedClients, allowedClientsResponse.data)
                ) {
                  store.dispatch(
                    setAllowedClients({ allowedClients: allowedClientsResponse.data })
                  );
                }
              }); //reload the clients
            }
          } else if (message?.startsWith("user-")) {
            const messageUserId = parseInt(message?.replace("user-", ""));
            const currentUserId = state.auth.currentUser?.userID; // current loggedin user
            if (currentUserId === messageUserId) {
              AuthenticationService.getCurrentUserInfo().then((userResponse) => {
                if (userResponse && userResponse.data) {
                  const currentUser = state.auth.currentUser;
                  if (!isEqual(currentUser, userResponse.data)) {
                    store.dispatch({
                      type: authTypes.SET_CURRENT_USER,
                      payload: { currentUser: { ...userResponse.data } },
                    });
                  }
                }
              });
            }
          }
        }

        console.log(`broadcastMessage: ${JSON.stringify(message)}`);
      });
    };

    const prevConnection = state.api.rConnection;
    if (prevConnection && prevConnection.stop) {
      prevConnection.stop().then(() => {
        initConnection();
      });
    } else {
      initConnection();
    }
  }

  handleIncomingMessage(message: any) {
    console.log("Received message from SignalR hub:", JSON.stringify(message));
    // Do something with the message
  }

  async loadLastAccessedClient() {
    let isAllowedForClient = true;
    const state = store.getState();
    let clientInfo: any = {};
    const {
      auth: { allowedClients },
    }: any = state;
    const lastClientId: any = window.localStorage.getItem("clientid");
    const hasPermissionsForClient = allowedClients.some((e: any) => e.clientID == lastClientId);
    let clientId = null;
    if (+lastClientId && hasPermissionsForClient) {
      clientId = lastClientId;
      clientInfo = await this.loadGlobalClient(+lastClientId);
    } else if (allowedClients.length > 0) {
      clientId = allowedClients[0].clientID;
      clientInfo = await this.loadGlobalClient(allowedClients[0].clientID);
    } else {
      isAllowedForClient = false;
    }
    return { isAllowedForClient, clientId, clientInfo };
  }

  async getUserNotifications(clientId: number): Promise<GetUserNotificationsResponse> {
    const result: GetUserNotificationsResponse = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/users/self/notifications`,
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to load notifications.",
      })
    );

    return result;
  }

  async markUserNotificationAsRead(clientId: number, userNotificationId: number) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/users/self/notifications/${userNotificationId}:markRead`,
        headers: { ...API.getAuthHeaders() },
        successMessage: "Notification marked as read.",
        failureMessage: "Failed to mark notification as read.",
      })
    );

    return result;
  }

  async deleteUserNotification(clientId: number, userNotificationId: number) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `v2/clients/${clientId}/users/self/notifications/${userNotificationId}`,
        headers: { ...API.getAuthHeaders() },
        successMessage: "Notification deleted.",
        failureMessage: "Failed to delete notification.",
      })
    );

    return result;
  }

  async subscriptionCheck(clientId: number) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/subscription`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return result?.data;
  }
}

const instance = new Clients();
export default instance;
